/**
 * LoginPage — Microsoft (Clutch staff) + Google (client admins)
 *
 * Route: /login
 * Redirects to /dashboard on successful login.
 *
 * Auth flow: Firebase popup → AuthContext fetches /auth/me to resolve
 * platform role. Unprovisioned accounts get a clear error message here
 * instead of being bounced to /dashboard.
 *
 * Navigation strategy: we do NOT call navigate() directly after signInWithPopup.
 * Instead a useEffect watches (loading, user, platformUser) from AuthContext and
 * navigates only once the full auth chain has settled. This eliminates the race
 * where ProtectedRoute sees loading=false / user=null between the Firebase null-fire
 * and the user-fire, which caused a first-click bounce back to /login.
 *
 * Providers:
 *   Microsoft OIDC — Clutch internal staff (Principal Consultant, Consultants)
 *   Google          — External client administrators
 *
 * TODO: replace MICROSOFT_OIDC_PROVIDER_ID with the provider ID assigned
 *       by Firebase Console → Authentication → Sign-in method → OIDC.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { signInWithPopup, signOut, GoogleAuthProvider, OAuthProvider } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { useAuth } from '../context/AuthContext'
import styles from './LoginPage.module.css'

// Set this to the OIDC provider ID shown in Firebase Console after
// configuring the Microsoft / Entra ID provider (e.g. 'oidc.microsoft').
const MICROSOFT_OIDC_PROVIDER_ID = 'oidc.microsoft'

const googleProvider    = new GoogleAuthProvider()
const microsoftProvider = new OAuthProvider(MICROSOFT_OIDC_PROVIDER_ID)

type Provider = 'google' | 'microsoft'

export function LoginPage() {
  const navigate = useNavigate()
  const { user, platformUser, loading: authLoading } = useAuth()

  const [error,     setError]     = useState('')
  const [signingIn, setSigningIn] = useState<Provider | null>(null)

  // Navigate once the full auth chain (Firebase + /auth/me) has settled.
  // This runs after every change to loading/user/platformUser so it catches
  // both fresh logins and page-load sessions (e.g. refresh while already logged in).
  useEffect(() => {
    if (authLoading) return          // still resolving — wait
    if (!user) return                // not logged in — stay on login page
    if (platformUser) {
      navigate('/dashboard', { replace: true })
    } else {
      // Firebase auth succeeded but this account isn't in platform_users.
      // Sign them back out so the session is clean for another attempt.
      setError("This account hasn't been provisioned. Contact your Clutch administrator.")
      signOut(auth)
    }
  }, [authLoading, user, platformUser, navigate])

  async function handleLogin(which: Provider) {
    setSigningIn(which); setError('')
    const provider = which === 'google' ? googleProvider : microsoftProvider
    try {
      await signInWithPopup(auth, provider)
      // Do NOT navigate here. The useEffect above handles navigation once
      // AuthContext has resolved loading=false and platformUser is set.
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      if (code !== 'auth/popup-closed-by-user') {
        setError(
          code === 'auth/too-many-requests'
            ? 'Too many attempts. Try again in a few minutes.'
            : 'Sign-in failed. Please try again.'
        )
      }
    } finally {
      setSigningIn(null)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Branding */}
        <div className={styles.logo}>
          <span className={styles.logoIcon}>◈</span>
          <span className={styles.logoWord}>Clutch</span>
        </div>
        <p className={styles.subtitle}>OCM Assessment Suite</p>

        {/* Sign-in buttons */}
        <div className={styles.ssoGroup}>

          {/* Microsoft — Clutch internal staff */}
          <button
            className={`${styles.ssoBtn} ${styles.ssoBtnMicrosoft}`}
            onClick={() => handleLogin('microsoft')}
            disabled={signingIn !== null}
            type="button"
          >
            <svg className={styles.ssoIcon} viewBox="0 0 24 24" aria-hidden>
              <rect x="1"  y="1"  width="10" height="10" fill="#F35325"/>
              <rect x="13" y="1"  width="10" height="10" fill="#81BC06"/>
              <rect x="1"  y="13" width="10" height="10" fill="#05A6F0"/>
              <rect x="13" y="13" width="10" height="10" fill="#FFBA08"/>
            </svg>
            {signingIn === 'microsoft' ? 'Signing in…' : 'Continue with Microsoft'}
          </button>

          <div className={styles.divider}>or</div>

          {/* Google — external client administrators */}
          <button
            className={`${styles.ssoBtn} ${styles.ssoBtnGoogle}`}
            onClick={() => handleLogin('google')}
            disabled={signingIn !== null}
            type="button"
          >
            <svg className={styles.ssoIcon} viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {signingIn === 'google' ? 'Signing in…' : 'Continue with Google'}
          </button>

        </div>

        {error && (
          <div className={styles.errorBox} role="alert">{error}</div>
        )}

        <p className={styles.helpNote}>
          Clutch staff sign in with Microsoft · Client admins sign in with Google.<br />
          Access is provisioned by your Clutch administrator.
        </p>

        <p className={styles.poweredBy}>
          Powered by <strong>Mosaic Data Solutions</strong>
        </p>

      </div>
    </div>
  )
}
